import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import deliveriesService from '../services/deliveries-service';

export default function DeliveriesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const load = async (p = 1) => {
    setLoading(true);
    setPage(p);
    try {
      const resp = await deliveriesService.list({
        page: p,
        limit,
        search: search.trim(),
        from: from ? `${from}T00:00:00` : '',
        to: to ? `${to}T23:59:59` : ''
      });
      if (resp && resp.success) {
        setRows(resp.data || []);
        setTotal(resp.total || 0);
      } else {
        setRows([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
      setRows([]);
      setTotal(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this delivery record? This cannot be undone.')) {
      return;
    }
    try {
      const resp = await deliveriesService.remove(id);
      if (resp && resp.success) {
        // Reload the current page (step back if it just emptied).
        const nextPage = rows.length === 1 && page > 1 ? page - 1 : page;
        load(nextPage);
      } else {
        alert(resp?.reason || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Delete failed');
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

  const fmtLocation = (d) => {
    if (d.captured_at_address) return d.captured_at_address;
    if (d.captured_at_latitude != null && d.captured_at_longitude != null) {
      return `${Number(d.captured_at_latitude).toFixed(5)}, ${Number(
        d.captured_at_longitude
      ).toFixed(5)}`;
    }
    return '—';
  };

  return (
    <div style={{ marginTop: 40, padding: 20 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Deliveries
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        On-demand driver deliveries (DoorDash / Uber Eats, etc.) — proof of hand-off.
      </Typography>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2
        }}
      >
        <TextField
          size="small"
          label="Search (PO, serial, service, user)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(1)}
          sx={{ minWidth: 280 }}
        />
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <Button variant="contained" onClick={() => load(1)}>
          Search
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setSearch('');
            setFrom('');
            setTo('');
            load(1);
          }}
        >
          Reset
        </Button>
        <Tooltip title="Refresh">
          <IconButton onClick={() => load(page)}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Captured By</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Captured At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No deliveries found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((d) => (
                  <TableRow
                    key={d.id}
                    hover
                    onClick={() => navigate(`/delivery-details/${d.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{d.id}</TableCell>
                    <TableCell>
                      <code>{d.po_number}</code>
                    </TableCell>
                    <TableCell>{d.serial_number || '—'}</TableCell>
                    <TableCell>
                      {d.service ? (
                        <Chip size="small" label={d.service} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{d.user?.username || '—'}</TableCell>
                    <TableCell>{fmtLocation(d)}</TableCell>
                    <TableCell>{fmtDate(d.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(d.id);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 2
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {total} total · Page {page} of {totalPages}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={page <= 1 || loading}
          onClick={() => load(page - 1)}
        >
          Prev
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={page >= totalPages || loading}
          onClick={() => load(page + 1)}
        >
          Next
        </Button>
      </Box>
    </div>
  );
}
